import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLSelectElement} [variantSelect] - Variant select dropdown
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {IntersectionObserver | null} */
  #titleAndPriceObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  #pendingAdd = false;
  #addToCartLabel = '';

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#addToCartLabel = this.refs.addToCartButton
      .querySelector('.add-to-cart-text__content > span')?.textContent?.trim() || 'Add to cart';

    this.#setupIntersectionObserver();
    this.#setupTitleAndPriceObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // Defer to next frame so the variant-picker element is fully connected and queryable
    requestAnimationFrame(() => this.#preselectDefaultSize());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#titleAndPriceObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * On mobile, if no variant is specified in the URL, preselect the configured default size.
   * Falls back to first available option if the default size is unavailable.
   */
  #preselectDefaultSize() {
    // Only preselect on mobile
    if (window.innerWidth >= 750) return;

    // Don't preselect if a variant is already explicitly selected via URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('variant')) return;

    const defaultSize = this.dataset.preselectSize;
    if (!defaultSize) return;

    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantPicker) return;

    // Find the size fieldset by checking legend text
    const fieldsets = variantPicker.querySelectorAll('fieldset');
    /** @type {HTMLFieldSetElement | null} */
    let sizeFieldset = null;

    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend');
      if (legend && legend.textContent.toLowerCase().includes('size')) {
        sizeFieldset = /** @type {HTMLFieldSetElement} */ (fieldset);
        break;
      }
    }

    if (!sizeFieldset) return;

    const radios = /** @type {HTMLInputElement[]} */ (Array.from(sizeFieldset.querySelectorAll('input[type="radio"]')));

    // Try to find a radio matching the default size that is available
    let targetRadio = radios.find(
      (radio) => radio.value === defaultSize && radio.dataset.optionAvailable === 'true'
    );

    // If not found or not available, fall back to first available
    if (!targetRadio) {
      targetRadio = radios.find((radio) => radio.dataset.optionAvailable === 'true');
    }

    if (targetRadio && !targetRadio.checked) {
      targetRadio.checked = true;
      targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('.footer-legal') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        this.#showStickyBar();
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Sets up the IntersectionObserver to watch the product title and price visibility
   */
  #setupTitleAndPriceObserver() {
    const titleLink = document.querySelector('.product-information h1');

    if (!titleLink) {
      this.dataset.titleHidden = 'true';
      return;
    }

    this.#titleAndPriceObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      this.dataset.titleHidden = (!entry.isIntersecting).toString();
    });

    this.#titleAndPriceObserver.observe(titleLink);
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (this.#pendingAdd) return;
    this.#pendingAdd = true;
    try {
      // A new/private visit can still be fetching the default size. Do not drop
      // that first tap or submit a stale form/button after a variant re-render.
      this.#targetAddToCartButton = await this.#waitForReadyAddButton();
      if (!this.#targetAddToCartButton) {
        const label = this.refs.addToCartButton.querySelector('.add-to-cart-text__content > span');
        if (label) label.textContent = 'Try again';
        return;
      }
      this.#restoreAddButton();
      this.#targetAddToCartButton.dataset.puppet = 'true';
      this.#targetAddToCartButton.click();
      const cartIcon = document.querySelector('.header-actions__cart-icon');

      if (this.refs.addToCartButton.dataset.added !== 'true') {
        this.refs.addToCartButton.dataset.added = 'true';
      }

      if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
      if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
      const sourceStyles = getComputedStyle(this.refs.productImage);

      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = this.refs.productImage;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);

      await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 800);
    } finally {
      this.#pendingAdd = false;
      this.removeAttribute('aria-busy');
      this.refs.addToCartButton.disabled = this.dataset.variantAvailable !== 'true';
    }
  };

  /** Wait for the currently displayed size to reach the live product form. */
  async #waitForReadyAddButton() {
    const deadline = Date.now() + 10000;
    while (this.isConnected) {
      const form = this.#getProductForm();
      const button = form?.querySelector('[ref="addToCartButton"]');
      const variantId = form?.querySelector('input[name="id"]')?.value;
      const displayedId = this.refs.variantSelect?.value || this.dataset.currentVariantId;
      if (button && !button.disabled && variantId === displayedId && typeof form.handleSubmit === 'function') {
        return button;
      }
      if (Date.now() >= deadline) break;
      this.setAttribute('aria-busy', 'true');
      this.refs.addToCartButton.disabled = true;
      const label = this.refs.addToCartButton.querySelector('.add-to-cart-text__content > span');
      if (label) label.textContent = 'Loading size…';
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return null;
  }

  #restoreAddButton() {
    this.removeAttribute('aria-busy');
    this.refs.addToCartButton.disabled = false;
    const label = this.refs.addToCartButton.querySelector('.add-to-cart-text__content > span');
    if (label) label.textContent = this.#addToCartLabel;
  }

  /**
   * Handles the variant select dropdown change in the sticky bar
   * Finds the corresponding radio/option in the main variant picker and triggers a change
   */
  handleVariantSelectChange = () => {
    const select = this.refs.variantSelect;
    if (!select) return;

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return;

    const variantId = selectedOption.value;
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantPicker) return;

    // Try to find a matching radio input by variant-id
    const matchingInput = /** @type {HTMLInputElement | null} */ (
      variantPicker.querySelector(`input[data-variant-id="${variantId}"]`)
    );

    if (matchingInput) {
      matchingInput.checked = true;
      matchingInput.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    // If no radio found (e.g. dropdown style variant picker), try a select element
    const selectElement = /** @type {HTMLSelectElement | null} */ (
      variantPicker.querySelector('select')
    );
    if (selectElement) {
      const matchingOption = /** @type {HTMLOptionElement | null} */ (
        selectElement.querySelector(`option[data-variant-id="${variantId}"]`)
      );
      if (matchingOption) {
        selectElement.value = matchingOption.value;
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;

      // Sync the variant select dropdown
      if (this.refs.variantSelect) {
        this.refs.variantSelect.value = String(variant.id);
      }
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    // If there's a variant select dropdown, deselect it
    if (this.refs.variantSelect) {
      this.refs.variantSelect.selectedIndex = -1;
      return;
    }

    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */(option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
